import React, { useState } from 'react';
import { Poppins } from 'src/common';
import Button from 'src/components/form/Button';
import { ConfirmDeleteModal } from 'src/components/modals';
import {
  useDeleteWorkspaceMutation,
  useArchiveWorkspaceMutation,
  useUnarchiveWorkspaceMutation,
  WorkspaceQuery,
} from 'src/graphql/_generated';
import styled from 'styled-components';
import { showSuccessToast, queueSuccessToast, toastMessages } from 'src/components/Toast';

const Div = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 13px;
  gap: 13px;
`;

interface DangerZoneProps {
  workspace: NonNullable<WorkspaceQuery['workspace']>;
  refetch: () => Promise<unknown>;
}

export const DangerZone: React.FC<DangerZoneProps> = ({ workspace, refetch }) => {
  const [archiveWarningModalOpen, setArchiveWarningModalOpen] = useState(false);
  const [unarchiveWarningModalOpen, setUnarchiveWarningModalOpen] = useState(false);
  const [deleteWarningModalOpen, setDeleteWarningModalOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);

  const [archiveWorkspace] = useArchiveWorkspaceMutation({
    variables: { id: workspace.id },
    update: (cache) => {
      cache.modify({
        id: cache.identify({ __typename: 'Workspace', id: workspace.id }),
        fields: { isArchived: () => true },
      });
    },
  });

  const [unarchiveWorkspace] = useUnarchiveWorkspaceMutation({
    variables: { id: workspace.id },
    update: (cache) => {
      cache.modify({
        id: cache.identify({ __typename: 'Workspace', id: workspace.id }),
        fields: { isArchived: () => false },
      });
    },
  });

  const [deleteWorkspace, { loading: isDeleting }] = useDeleteWorkspaceMutation({
    variables: { id: workspace.id },
    onCompleted: () => {
      queueSuccessToast(toastMessages.workspace.deleted);
      window.location.href = '/home';
    },
  });

  return (
    <>
      {archiveWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={async () => {
              setIsArchiving(true);
              await archiveWorkspace();
              await refetch();
              showSuccessToast(toastMessages.workspace.archived);
              setArchiveWarningModalOpen(false);
              setIsArchiving(false);
            }}
            onClose={() => setArchiveWarningModalOpen(false)}
            confirmText="ARCHIVE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to archive{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {workspace.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      {unarchiveWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={async () => {
              setIsUnarchiving(true);
              await unarchiveWorkspace();
              await refetch();
              showSuccessToast(toastMessages.workspace.unarchived);
              setUnarchiveWarningModalOpen(false);
              setIsUnarchiving(false);
            }}
            onClose={() => setUnarchiveWarningModalOpen(false)}
            confirmText="UNARCHIVE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to unarchive{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {workspace.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      {deleteWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={async () => {
              await deleteWorkspace();
            }}
            onClose={() => setDeleteWarningModalOpen(false)}
            confirmText="DELETE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to delete{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {workspace.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      <Div>
        {workspace.isArchived ? (
          <>
            {workspace.actionsOnThisWorkspace?.canUnarchive && (
              <Button
                secondary
                type="button"
                disabled={isUnarchiving}
                loading={isUnarchiving}
                $constWidth={220}
                onClick={() => setUnarchiveWarningModalOpen(true)}
              >
                UNARCHIVE
              </Button>
            )}
          </>
        ) : (
          <>
            {workspace.actionsOnThisWorkspace?.canArchive && (
              <Button
                secondary
                type="button"
                disabled={isArchiving}
                loading={isArchiving}
                $constWidth={220}
                onClick={() => setArchiveWarningModalOpen(true)}
              >
                ARCHIVE
              </Button>
            )}
          </>
        )}
        {(workspace.actionsOnThisWorkspace?.canArchive || workspace.actionsOnThisWorkspace?.canUnarchive) && (
          <Button
            secondary
            type="button"
            disabled={isDeleting || !workspace.actionsOnThisWorkspace?.canDelete}
            loading={isDeleting}
            $constWidth={220}
            onClick={() => setDeleteWarningModalOpen(true)}
          >
            DELETE
          </Button>
        )}
      </Div>
    </>
  );
};
